import Logger from '@cvent/nucleus-logging';

const LOG = new Logger('redux/modules/registrantLogin/actions');
import { unescape } from 'lodash';
import { restoreTransientTravelCartIntoState, restoreTravelCartIntoState } from '../travelCart/external';
import { filterEventSnapshot } from '../actions';
import { loadAvailableCapacityCounts } from '../capacity';
import { addMultipleSessionsForSelectingWaitlist } from '../sessionsInWaitlist';
import {
  SET_LOGIN_FORM_FIELD,
  RESEND_CONFIRMATION_PENDING,
  RESEND_CONFIRMATION_SUCCESS,
  RESEND_CONFIRMATION_FAILURE,

  LOG_IN_REGISTRANT_SUCCESS,
  LOG_IN_REGISTRANT_FAILURE,

  LOG_OUT_REGISTRANT_PENDING,
  LOG_OUT_REGISTRANT_SUCCESS,
  LOG_OUT_REGISTRANT_FAILURE,

  RESET_STATUS
} from './actionTypes';

export const setLoginFormField = (fieldName, value) => {
  return { type: SET_LOGIN_FORM_FIELD, payload: { fieldName, value } };
};

/**
 * Attempts to log in a registrant. If the login attempt fails a user friendly
 * message along with the technical error will be stored within the application.
 */
export function loginRegistrant(confirmationInfo) {
  return async (dispatch, getState) => {
    const {
      clients: { regCartClient },
      accessToken,
      userSession: { eventId },
      text: { translate }
    } = getState();
    let {
      registrantLogin: { form: { emailAddress, confirmationNumber } }
    } = getState();
    try {
      if (confirmationInfo) {
        emailAddress = confirmationInfo.emailAddress;
        confirmationNumber = confirmationInfo.confirmationNumber;
      }
      LOG.debug('loginRegistrant', eventId, emailAddress, confirmationNumber);
      const response = await regCartClient.identifyByConfirm(accessToken, eventId, emailAddress, confirmationNumber);
      LOG.debug('loginRegistrant success', response);
      const regCart = response.regCart;
      dispatch({ type: LOG_IN_REGISTRANT_SUCCESS, payload: { regCart: { ...regCart, regRetrieval: true } } });

      if (regCart && regCart.eventRegistrations) {
        const { registrationPathId, registrationTypeId } = Object.values(regCart.eventRegistrations)[0];
        await dispatch(filterEventSnapshot(getState().eventSnapshotVersion, registrationTypeId, registrationPathId));
        await dispatch(loadAvailableCapacityCounts());

        // Creating a list of sessions which are waitlisted
        let waitlistedSessions = {};
        Object.keys(regCart.eventRegistrations).forEach((id) => {
          Object.keys(regCart.eventRegistrations[id].sessionWaitlists).forEach((sessionId) => {
            waitlistedSessions[sessionId] = true;
          });
        });
        dispatch(addMultipleSessionsForSelectingWaitlist(waitlistedSessions));
      }

      if (regCart && regCart.regCartId && regCart.hasTravel) {
        await dispatch(restoreTravelCartIntoState(regCart.regCartId));
      } else {
        await dispatch(restoreTransientTravelCartIntoState());
      }
    } catch (error) {
      LOG.info('loginRegistrant failed', error);
      const { responseStatus, responseBody = {}, httpLogRequestId } = error;
      const firstValidationMessage = (responseBody.validationMessages || [])[0] || {};
      let errorMessage;
      if (!responseStatus) {
        LOG.error('loginRegistrant network error', error);
        errorMessage = translate('EventWidgets_GenericErrors_NetworkError__resx');
      } else if (firstValidationMessage.localizationKey === 'REGAPI.LOOKUP_REGCART_BY_CONFIRM_NOT_FOUND') {
        errorMessage = translate('EventWidgets_AlreadyRegistered_ConfirmationNumberDoesNotExist__resx');
      } else if (firstValidationMessage.localizationKey === 'REGAPI.LOOKUP_REGCART_BY_CONFIRM_EMAIL_MISMATCH') {
        errorMessage = translate('EventWidgets_AlreadyRegistered_EmailConfirmationNumberMismatch__resx');
      } else {
        LOG.error('loginRegistrant unexpected error', error);
        errorMessage = translate('EventWidgets_GenericErrors_UnexpectedError__resx', { instanceId: httpLogRequestId });
      }
      dispatch({ type: LOG_IN_REGISTRANT_FAILURE, payload: { error, errorMessage } });
      throw error;
    }
  };
}
/**
 * Clears the registrant's session service side and dispatches an action
 * to indicate all registrant data should be removed.
 */
export const logoutRegistrant = () => {
  return async (dispatch, getState) => {
    const {
      clients: { eventGuestClient },
      accessToken,
      event: { id: eventId },
      userSession: { isPreview }
    } = getState();
    dispatch({ type: LOG_OUT_REGISTRANT_PENDING });
    try {
      await eventGuestClient.logout(accessToken, eventId, isPreview ? 'preview' : 'standard');
      dispatch({ type: LOG_OUT_REGISTRANT_SUCCESS });
    } catch (error) {
      dispatch({ type: LOG_OUT_REGISTRANT_FAILURE, payload: { error } });
      throw error;
    }
  };
};

/**
 * Clears the registrant's session service side and redirect to a different page,
 * note that this does not dispatche action to clear registrant's data from state
 * to avoid a flashing invalid data before the redirect.
 */
export const logoutPlanner = (redirectUrl) => {
  return async (dispatch, getState) => {
    const {
      clients: { eventGuestClient },
      accessToken,
      event: { id: eventId }
    } = getState();
    try {
      await eventGuestClient.logout(accessToken, eventId, 'planner');
      global.location.replace(unescape(redirectUrl));
    } catch (error) {
      dispatch({ type: LOG_OUT_REGISTRANT_FAILURE, payload: { error } });
      throw error;
    }
  };
};

/**
 * Attempts to resend the users confirmation email.
 */
export function resendConfirmationEmail() {
  return async (dispatch, getState) => {
    const {
      clients: { eventEmailClient },
      accessToken,
      event,
      registrantLogin: { form: { emailAddress, firstName, lastName } }
    } = getState();
    dispatch({ type: RESEND_CONFIRMATION_PENDING });
    try {
      await eventEmailClient.resendConfirmationEmail(accessToken, event.id, emailAddress, firstName, lastName);
      dispatch({ type: RESEND_CONFIRMATION_SUCCESS });
    } catch (error) {
      LOG.error('resendConfirmationEmail failed', error);
      dispatch({ type: RESEND_CONFIRMATION_FAILURE, payload: { error } });
    }
  };
}

export function resetStatus() {
  return { type: RESET_STATUS };
}
